import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PageSeo } from "../components/seo/PageSeo";
import { JsonLd } from "../components/seo/JsonLd";
import { Link } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import {
  interactiveDemoCtaLabel,
  interactiveDemoHref,
} from "@/config/platformAccess";
import { BackdropHero } from "@/components/marketing/BackdropHero";
import { FourLayersSection } from "@/components/marketing/landing/home/FourLayersSection";
import { ThreeFlowsSection } from "@/components/marketing/landing/home/ThreeFlowsSection";
import { TraditionalVsCaireSection } from "@/components/marketing/landing/home/TraditionalVsCaireSection";
import { ServiceExceptionSection } from "@/components/marketing/landing/home/ServiceExceptionSection";
import { RoleSection } from "@/components/marketing/landing/home/RoleSection";
import {
  homeLinkClass,
  homeSectionClass,
} from "@/components/marketing/landing/home/HomeParts";
import { getLocalizedPath } from "@/utils/routes";
import { AnalysisCta } from "@/components/marketing/AnalysisCta";
import {
  bookDemoCtaLabel,
  bookDemoHref,
  BOOK_DEMO_TARGET,
  BOOK_DEMO_REL,
} from "@/config/platformAccess";
import { trackMarketingCta } from "@/lib/marketingCta";
import { approvedProductImageFile } from "@/components/marketing/product-shot/websiteProductImage";

const SaFungerarCaire: React.FC = () => {
  const { t, i18n, ready: loaded } = useTranslation("saFungerarCaire");
  // A server-seeded bundle is enough to render (avoids a hydration mismatch).
  const ready =
    loaded || i18n.hasResourceBundle(i18n.language, "saFungerarCaire");
  const currentLang = i18n.language;
  const isSwedish = currentLang === "sv";
  const locale = isSwedish ? "sv" : "en";

  const contactPath = isSwedish ? "/kontakt" : "/en/contact";
  const canonicalPath = isSwedish
    ? "/sa-fungerar-caire"
    : "/en/how-caire-works";

  useEffect(() => {
    if (!i18n.hasResourceBundle(currentLang, "saFungerarCaire")) {
      i18n.loadNamespaces("saFungerarCaire");
    }

    const pageTitle = t(
      "seo.title",
      "Så fungerar CAIRE – Revolutionerande hemtjänstplanering",
    );
    trackPageView(canonicalPath, pageTitle);
  }, [currentLang, t, i18n, canonicalPath]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-caire-page text-caire-ink flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-coral mx-auto mb-4"></div>
          <p className="text-xl text-slate">Laddar...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageSeo
        title={t("seo.title")}
        description={t("seo.description")}
        canonicalPath={canonicalPath}
        ogImage="/images/optimized/caire-AI-hero-1920.png"
        // PageSeo auto-generates a WebPage unless it is given one, so the
        // separate <JsonLd type="WebPage"> here shipped two nodes for this URL.
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: t("seo.title"),
          description: t("seo.description"),
          url: `https://www.caire.se${canonicalPath}`,
        }}
      />
      <JsonLd
        type="SoftwareApplication"
        data={{
          name: "CAIRE",
          image: "https://www.caire.se/images/optimized/caire-AI-hero-1920.png",
          applicationCategory: "Healthcare Software",
          operatingSystem: "Web-based",
          description: t("seo.description"),
          // Google requires one of offers / aggregateRating / review; this node
          // had none, so it was an invalid item. Real public entry price from
          // /prissattning — no invented rating, there are no public reviews.
          offers: {
            "@type": "Offer",
            price: "9900",
            priceCurrency: "SEK",
            availability: "https://schema.org/InStock",
            url: `https://www.caire.se${isSwedish ? "/prissattning" : "/en/pricing"}`,
          },
        }}
      />
      <JsonLd
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: isSwedish ? "Hem" : "Home",
              item: isSwedish
                ? "https://www.caire.se/"
                : "https://www.caire.se/en",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("seo.title"),
              item: `https://www.caire.se${canonicalPath}`,
            },
          ],
        }}
      />

      <BackdropHero
        id="how-hero"
        eyebrow={isSwedish ? "Så fungerar Caire" : "How Caire works"}
        title={t("hero.title")}
        description={t("hero.subtitle")}
        image={approvedProductImageFile(
          "standing-data.care-plan-visits",
          locale,
        )}
        note={isSwedish ? "från beslut till besök" : "from decision to visit"}
        actions={
          <>
            <AnalysisCta
              locale={locale}
              cta="analysis_hero"
              label={
                locale === "sv"
                  ? "Prova Caire kostnadsfritt"
                  : "Try Caire for free"
              }
            />
            <a
              href={bookDemoHref(locale)}
              target={BOOK_DEMO_TARGET}
              rel={BOOK_DEMO_REL}
              onClick={() =>
                trackMarketingCta(
                  "how_booking",
                  bookDemoCtaLabel(locale),
                  bookDemoHref(locale),
                )
              }
              className="inline-flex min-h-12 items-center rounded-lg border border-hairline px-6 py-3 text-sm font-semibold"
            >
              {bookDemoCtaLabel(locale)}
            </a>
          </>
        }
        footer={
          <>
            {isSwedish
              ? "Organisationens behörigheter och regler styr vem som ser och ändrar planeringen."
              : "Your organisation's permissions and rules govern who sees and changes the plan."}{" "}
            <Link
              to={getLocalizedPath("help.securityAndPersonalData", locale)}
              className="font-semibold text-caire-action underline underline-offset-4"
            >
              {isSwedish
                ? "Granska trygghet och dataskydd"
                : "Review trust and data protection"}
            </Link>
          </>
        }
      />

      {/* Brand 2.2: the model, the flows, why it matters and the exception
          rule. The product lifecycle in detail lives on /plattform. */}
      <div className="bg-caire-page text-caire-ink" lang={locale}>
        <FourLayersSection locale={locale} showLink={false} />
        <ThreeFlowsSection locale={locale} />
        <TraditionalVsCaireSection locale={locale} />
        <ServiceExceptionSection locale={locale} />
        <RoleSection locale={locale} />
        <div className={`${homeSectionClass} pt-0`}>
          <Link
            to={getLocalizedPath("plattform", locale)}
            className={homeLinkClass}
          >
            {isSwedish
              ? "Se planeringens gång i plattformen"
              : "See the planning lifecycle in the platform"}
          </Link>
        </div>
      </div>

      {/* Call to Action */}
      <section className="bg-caire-sand py-16">
        <div className="mx-auto max-w-[80rem] px-6 lg:px-8 sm:px-6 md:px-8 max-w-5xl text-center">
          <h3 className="text-2xl font-semibold mb-4 text-caire-ink">
            {t("cta.title")}
          </h3>
          <p className="text-lg text-slate mb-6">{t("cta.description")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <AnalysisCta
              locale={locale}
              cta="analysis_contextual"
              label={
                locale === "sv"
                  ? "Prova Caire kostnadsfritt"
                  : "Try Caire for free"
              }
            />
            <a
              href={bookDemoHref(locale)}
              target={BOOK_DEMO_TARGET}
              rel={BOOK_DEMO_REL}
              onClick={() =>
                trackMarketingCta(
                  "how_closing_booking",
                  bookDemoCtaLabel(locale),
                  bookDemoHref(locale),
                )
              }
              className="inline-flex items-center justify-center rounded-lg border border-hairline px-5 py-3 text-sm font-semibold"
            >
              {bookDemoCtaLabel(locale)}
            </a>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-5">
            <a
              href={interactiveDemoHref(locale)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--cb-action)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-caire-action-hover"
            >
              {interactiveDemoCtaLabel(locale)}
            </a>
            <Link
              to={contactPath}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--cb-border-soft)] bg-white px-5 py-3 text-sm font-semibold text-caire-ink transition hover:bg-caire-sand"
            >
              {t("cta.contact")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default SaFungerarCaire;
